import React, { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Container } from 'reactstrap';
import { useIntl } from 'react-intl';
import { Redirect, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';
import { Location, LocationDescriptorObject } from 'history';
import { PUBLIC_NAV_ITEMS, SOURCE, SOURCE_EMAIL, SOURCE_SMS } from '@whitelabel/xcover-shared/helpers/constants';
import { storeCustomerRegionFromURL, configureCognito } from '@whitelabel/xcover-shared/helpers/multiRegion';
import { deleteFalsyParams } from '@whitelabel/helpers/url';
import { validateSignUpToken } from '../../../actions';
import NavBar from '../../layout/NavBar';
import PageMeta from '../../helpers/PageMeta';
import XpayPaypalAlert from '../../../components/XpayPaypalAlert';
import ErrorPage from '../../../components/ErrorPage';
import commonMessages from '../../../helpers/messages/commonMsg';
import StyledLoading from '../../../styled/StyledLoading';
import Signup from '../../../components/Signup/Signup';
import { RootState } from '../../../store';
import Footer from '../../layout/Footer';
import { StyledLayout, StyledSignUpBlock, StyledBGWrapper } from './styledConfirmSignUp';
import messages from './messages';
import { useSignUpToken } from './helper';

const ConfirmSignUp = (): JSX.Element => {
  const theme = useTheme();
  const isMobile = !useMediaQuery({ minWidth: theme.gridBreakpoints.sm });
  const intl = useIntl();
  const dispatch = useDispatch();
  const location: Location<{ referrer?: LocationDescriptorObject }> = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');
  const token = urlParams.get('token');
  const securityToken = urlParams.get('securityToken');
  const emailToken = urlParams.get('emailToken');
  const referrer = urlParams.get('referrer');
  const signUpToken = urlParams.get('signupToken');
  const sourceParam = urlParams.get(SOURCE);
  const region = urlParams.get('region');

  useEffect(() => {
    storeCustomerRegionFromURL();
    configureCognito();
  }, []);

  const { user, signingIn, loadingCustomer, customerError, customerData, validateSignUpTokenError } = useSelector(
    ({
      user: { data: userData, signingIn: userSigning },
      customer: { loading, error, data, validateSignUpTokenError: customerValidateSignUpTokenError },
    }: RootState) => ({
      user: userData,
      signingIn: userSigning,
      loadingCustomer: loading,
      customerError: error,
      customerData: data,
      validateSignUpTokenError: customerValidateSignUpTokenError,
    }),
    shallowEqual,
  );

  const [tokenSource, maskedSignUpTokenValid, jwtCustomer] = useSignUpToken(signUpToken, intl.locale, token);

  let customer = customerData || jwtCustomer;
  if (customer?.id !== id) {
    customer = undefined;
  }

  const { pathname, search } = location.state?.referrer || {};
  const referralSearch = new URLSearchParams(search);
  referralSearch.delete('signup_token');
  referralSearch.delete('id');
  const stateReferrer = {
    referrer: {
      pathname,
      search: referralSearch.toString(),
    },
  };

  const source = sourceParam || tokenSource || SOURCE_EMAIL;

  const tokenErrorMessage = intl.formatMessage(
    source === SOURCE_SMS ? messages.invalidToken : messages.invalidTokenEmail,
  );

  // Redirect to login after storing clean (No ID & Token)
  // search params in state to be actioned after login
  const redirectToLogin = (mode: string) => {
    const emailParam = urlParams.get('email');
    let referrerObj = {};

    if (stateReferrer.referrer.pathname) {
      referrerObj = stateReferrer;
    } else if (emailParam) {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.set('email', emailParam);
      referrerObj = {
        referrer: {
          pathname: location.pathname,
          search: urlSearchParams.toString(),
        },
      };
    }

    return (
      <Redirect
        to={{
          pathname: `/${intl.locale}/login`,
          state: {
            message: intl.formatMessage(
              mode === SOURCE_EMAIL ? commonMessages.emailVerified : commonMessages.phoneVerified,
            ),
            source,
            ...referrerObj,
          },
        }}
      />
    );
  };

  // Redirect to destination with clean (No ID & Token) search params
  const redirectToDestination = (destination: string) => (
    <Redirect
      to={{
        pathname: destination,
        search: referralSearch.toString(),
      }}
    />
  );

  // Customer landed, validate the id and token
  useEffect(() => {
    if (id && token && !customer?.token) {
      dispatch(validateSignUpToken({ id, token, source }));
    }
  }, [id, token, source, customer, dispatch]);

  // If user is coming back through a link that has a referrer query, store it in state through a redirect
  if (referrer) {
    return (
      <Redirect
        to={{
          pathname: `/${intl.locale}/signup/confirm`,
          search: `${new URLSearchParams(deleteFalsyParams({ id, token, source, region }))})`,
          state: { referrer: JSON.parse(referrer) },
        }}
      />
    );
  }

  /*
    In general, below are 3 kinds of valid query params combinations for a sign up url:
      1. id, token (this is regular existing sign up url params)
      2. id, securityToken, signUpToken (this is a signup flow where we don't have the customer email/phone details)
      3. id, securityToken, emailToken (this is to support the old amazon flow)
  */
  if (
    customerError ||
    (validateSignUpTokenError && validateSignUpTokenError.status !== 422) ||
    !id ||
    !(token || (securityToken && signUpToken) || (securityToken && emailToken))
  ) {
    return <ErrorPage error={customerError || validateSignUpTokenError} />;
  }

  if (
    !signingIn &&
    ((validateSignUpTokenError && validateSignUpTokenError.status === 422) ||
      (source === SOURCE_SMS && customer?.phoneVerified) ||
      (source === SOURCE_EMAIL && customer?.emailVerified))
  ) {
    if (user) {
      return redirectToDestination(pathname || `/${intl.locale}/account`);
    }
    return redirectToLogin(source);
  }

  // Hold a loading state briefly till we confirm signup token and decide
  if (user || (!customer && maskedSignUpTokenValid === undefined)) {
    return <StyledLoading />;
  }

  const navBar = <NavBar items={PUBLIC_NAV_ITEMS} size="lg" />;

  const renderSignUpBlock = () => (
    <Container>
      <StyledSignUpBlock sm="8" lg="7">
        <XpayPaypalAlert />
        <Signup
          id={id}
          source={source as typeof SOURCE_SMS | typeof SOURCE_EMAIL}
          loading={loadingCustomer}
          maskedSignUpTokenValid={maskedSignUpTokenValid}
          signUpToken={signUpToken as string}
          tokenErrorMessage={tokenErrorMessage}
          autoFocusPasswordField
          customer={customer}
        />
      </StyledSignUpBlock>
    </Container>
  );

  const renderMobileDOM = () => (
    <>
      <StyledBGWrapper $rtl={intl.bidi} />
      {renderSignUpBlock()}
    </>
  );

  const renderNonMobileDOM = () => <StyledBGWrapper $rtl={intl.bidi}>{renderSignUpBlock()}</StyledBGWrapper>;

  return (
    <StyledLayout navBar={navBar} footer={<Footer />} spacing="lg">
      <PageMeta seoTitle={intl.formatMessage(messages.seoTitle)} />
      {isMobile ? renderMobileDOM() : renderNonMobileDOM()}
    </StyledLayout>
  );
};

export default ConfirmSignUp;
